import { Link } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import ThemeToggle from './ThemeToggle';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const closeDropdown = () => {
    setActiveDropdown(null);
  };

  const handleAnchorNavigation = (href: string) => {
    // Check if this is an anchor link to the same page
    const [path, hash] = href.split('#');
    const currentPath = window.location.pathname;
    
    if (path === currentPath && hash) {
      // We're already on the target page, just scroll to the element
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100); // Small delay to ensure dropdown closes first
    }
    
    closeDropdown();
  };

  const handleMouseEnter = (dropdownName: string) => {
    // Clear any pending close timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setActiveDropdown(dropdownName);
    setIsAnimating(true);
  };

  const handleMouseLeave = () => {
    // Add a small delay before closing to allow users to move to dropdown
    setIsAnimating(false);
    timeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 150); // 150ms delay
  };

  // Clean up timeout on component unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const servicesDropdown = [
    { 
      href: '/services#consulting', 
      label: 'Consulting', 
      subtext: 'Strategic guidance to navigate digital transformation and technology decisions'
    },
    { 
      href: '/services#software-design', 
      label: 'Software Design', 
      subtext: 'User-centered design of digital platforms for institutional needs'
    },
    { 
      href: '/services#prototyping', 
      label: 'Prototyping', 
      subtext: 'Rapid development of proof-of-concept solutions to validate ideas'
    },
    { 
      href: '/services#custom-development', 
      label: 'Custom Development', 
      subtext: 'Full-scale development of tailored software solutions'
    },
    { 
      href: '/services#training', 
      label: 'Training', 
      subtext: 'Comprehensive programs to help staff maximize digital systems'
    },
    { href: '/services', label: 'More', isEmphasized: false },
  ];

  const expertiseDropdown = [
    {
      href: '/expertise#knowledge-management',
      label: 'Knowledge Management',
      subtext: 'Organize and discover information across your organization'
    },
    {
      href: '/expertise#information-extraction',
      label: 'Information Extraction',
      subtext: 'Extract key data from documents and unstructured sources'
    },
    {
      href: '/expertise#ai-agents',
      label: 'AI Agents',
      subtext: 'Build intelligent assistants that interact with users naturally'
    },
    {
      href: '/expertise#mcp-servers',
      label: 'MCP Servers',
      subtext: 'Extend AI capabilities with custom Model Context Protocol servers'
    },
    {
      href: '/expertise#natural-language-processing',
      label: 'Natural Language Processing',
      subtext: 'Understand and analyze human language at scale'
    },
    {
      href: '/expertise#machine-learning',
      label: 'Machine Learning',
      subtext: 'Learn from your data'
    },
    {
      href: '/expertise#business-automation',
      label: 'Business Automation',
      subtext: 'Streamline repetitive tasks and reduce manual errors'
    },
    {
      href: '/expertise#cloud-technologies',
      label: 'Cloud Technologies',
      subtext: 'Scale and secure your systems with modern cloud infrastructure'
    },
    {
      href: '/expertise#data-visualization',
      label: 'Data Visualization',
      subtext: 'Transform complex data into clear, interactive visual stories'
    },
    {
      href: '/contact',
      label: "Don't see what you're looking for? Contact Us",
      subtext: null,
      isEmphasized: false
    },
  ];

  const caseStudiesDropdown = [
    {
      href: '/case-studies#pinball-publishing',
      label: 'AI-Powered Access to NYC History',
      subtext: 'See how we transformed Pinball Publishing\'s collection of historical documents into a modern AI application and data platform.'
    },
    { href: '/case-studies', label: 'All Case Studies' },
  ];

  const demosDropdown = [
    {
      href: '/demos/art-search',
      label: 'Art Search',
      subtext: 'Search 116k+ artworks with AI-powered semantic search using natural language or image similarity.'
    },
  ];

  const simpleNavItems = [
    { href: '/', label: 'Home' },
    { href: '/about', label: 'About' },
    { href: '/contact', label: 'Contact' },
  ];

  return (
    <header className="border-b border-gray-200 dark:border-gray-800">
      <div className="container mx-auto px-4 py-4 max-w-7xl">
        <nav className="flex justify-between items-center" ref={dropdownRef}>
          <Link to="/" className="flex items-center space-x-2">
            <img
              src="/images/interface-research-logo-light-no-background.png"
              alt="Interface Research"
              className="h-24 block dark:hidden"
            />
            <img
              src="/images/interface-research-logo-dark-no-background.png"
              alt="Interface Research"
              className="h-24 hidden dark:block"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {/* Simple nav items */}
            {simpleNavItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className="text-glam-secondary dark:text-gray-300 hover:text-glam-accent dark:hover:text-glam-accent transition-colors"
              >
                {item.label}
              </Link>
            ))}

            {/* Services Dropdown */}
            <div 
              className="relative py-2"
              onMouseEnter={() => handleMouseEnter('services')}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className="flex items-center text-glam-secondary dark:text-gray-300 hover:text-glam-accent dark:hover:text-glam-accent transition-colors"
              >
                Services
                <svg
                  className={`ml-1 h-4 w-4 transition-transform ${
                    activeDropdown === 'services' ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {activeDropdown === 'services' && (
                <div className={`absolute top-full left-0 mt-0 w-80 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-200 dark:border-gray-700 transition-all duration-200 ease-out ${
                  isAnimating 
                    ? 'opacity-100 transform translate-y-0' 
                    : 'opacity-0 transform -translate-y-2'
                }`}>
                  {servicesDropdown.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className="block px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      onClick={() => handleAnchorNavigation(item.href)}
                    >
                      <div className={`text-sm font-medium hover:text-glam-accent ${
                        item.isEmphasized === false 
                          ? 'text-xs text-gray-400 dark:text-gray-500' 
                          : 'text-glam-secondary dark:text-gray-300'
                      }`}>
                        {item.label}
                      </div>
                      {item.subtext && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                          {item.subtext}
                        </div>
                      )}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Solutions Dropdown */}
            <div 
              className="relative py-2"
              onMouseEnter={() => handleMouseEnter('expertise')}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className="flex items-center text-glam-secondary dark:text-gray-300 hover:text-glam-accent dark:hover:text-glam-accent transition-colors"
              >
                Solutions
                <svg
                  className={`ml-1 h-4 w-4 transition-transform ${
                    activeDropdown === 'expertise' ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {activeDropdown === 'expertise' && (
                <div className={`absolute top-full left-1/2 transform -translate-x-1/2 mt-0 w-96 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-200 dark:border-gray-700 transition-all duration-200 ease-out ${
                  isAnimating 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 transform -translate-y-2'
                }`}>
                  {expertiseDropdown.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className="block px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      onClick={() => handleAnchorNavigation(item.href)}
                    >
                      <div className={`text-sm font-medium hover:text-glam-accent ${
                        item.isEmphasized === false 
                          ? 'text-xs text-gray-400 dark:text-gray-500' 
                          : 'text-glam-secondary dark:text-gray-300'
                      }`}>
                        {item.label}
                      </div>
                      {item.subtext && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                          {item.subtext}
                        </div>
                      )}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Case Studies Dropdown */}
            <div
              className="relative py-2"
              onMouseEnter={() => handleMouseEnter('case-studies')}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className="flex items-center text-glam-secondary dark:text-gray-300 hover:text-glam-accent dark:hover:text-glam-accent transition-colors"
              >
                Case Studies
                <svg
                  className={`ml-1 h-4 w-4 transition-transform ${
                    activeDropdown === 'case-studies' ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {activeDropdown === 'case-studies' && (
                <div className={`absolute top-full right-0 mt-0 w-80 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-200 dark:border-gray-700 transition-all duration-200 ease-out ${
                  isAnimating
                    ? 'opacity-100 transform translate-y-0'
                    : 'opacity-0 transform -translate-y-2'
                }`}>
                  {caseStudiesDropdown.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className="block px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      onClick={closeDropdown}
                    >
                      <div className="text-sm font-medium text-glam-secondary dark:text-gray-300 hover:text-glam-accent">
                        {item.label}
                      </div>
                      {item.subtext && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                          {item.subtext}
                        </div>
                      )}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Demos Dropdown */}
            <div
              className="relative py-2"
              onMouseEnter={() => handleMouseEnter('demos')}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className="flex items-center text-glam-secondary dark:text-gray-300 hover:text-glam-accent dark:hover:text-glam-accent transition-colors"
              >
                Demos
                <svg
                  className={`ml-1 h-4 w-4 transition-transform ${
                    activeDropdown === 'demos' ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {activeDropdown === 'demos' && (
                <div className={`absolute top-full right-0 mt-0 w-80 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-200 dark:border-gray-700 transition-all duration-200 ease-out ${
                  isAnimating
                    ? 'opacity-100 transform translate-y-0'
                    : 'opacity-0 transform -translate-y-2'
                }`}>
                  {demosDropdown.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className="block px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      onClick={closeDropdown}
                    >
                      <div className="text-sm font-medium text-glam-secondary dark:text-gray-300 hover:text-glam-accent">
                        {item.label}
                      </div>
                      {item.subtext && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                          {item.subtext}
                        </div>
                      )}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <ThemeToggle />
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center space-x-4">
            <ThemeToggle />
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-glam-primary dark:text-glam-light"
              aria-label="Toggle menu"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={
                    isMenuOpen
                      ? 'M6 18L18 6M6 6l12 12'
                      : 'M4 6h16M4 12h16M4 18h16'
                  }
                />
              </svg>
            </button>
          </div>
        </nav>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-gray-200 dark:border-gray-800 pt-4">
            {/* Simple mobile nav items */}
            {simpleNavItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className="block py-2 text-glam-secondary dark:text-gray-300 hover:text-glam-accent dark:hover:text-glam-accent transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}

            {/* Mobile Services Section */}
            <div className="py-2">
              <div className="font-semibold text-glam-primary dark:text-glam-light mb-2">Services</div>
              {servicesDropdown.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className="block py-2 pl-4 hover:text-glam-accent dark:hover:text-glam-accent transition-colors"
                  onClick={() => {
                    handleAnchorNavigation(item.href);
                    setIsMenuOpen(false);
                  }}
                >
                  <div className={`text-sm font-medium ${
                    item.isEmphasized === false 
                      ? 'text-xs text-gray-400 dark:text-gray-500' 
                      : 'text-glam-secondary dark:text-gray-300'
                  }`}>
                    {item.label}
                  </div>
                  {item.subtext && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                      {item.subtext}
                    </div>
                  )}
                </Link>
              ))}
            </div>

            {/* Mobile Solutions Section */}
            <div className="py-2">
              <div className="font-semibold text-glam-primary dark:text-glam-light mb-2">Solutions</div>
              {expertiseDropdown.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className="block py-2 pl-4 hover:text-glam-accent dark:hover:text-glam-accent transition-colors"
                  onClick={() => {
                    handleAnchorNavigation(item.href);
                    setIsMenuOpen(false);
                  }}
                >
                  <div className={`text-sm font-medium ${
                    item.isEmphasized === false 
                      ? 'text-xs text-gray-400 dark:text-gray-500' 
                      : 'text-glam-secondary dark:text-gray-300'
                  }`}>
                    {item.label}
                  </div>
                  {item.subtext && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                      {item.subtext}
                    </div>
                  )}
                </Link>
              ))}
            </div>

            {/* Mobile Case Studies Section */}
            <div className="py-2">
              <div className="font-semibold text-glam-primary dark:text-glam-light mb-2">Case Studies</div>
              {caseStudiesDropdown.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className="block py-2 pl-4 hover:text-glam-accent dark:hover:text-glam-accent transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <div className="text-sm font-medium text-glam-secondary dark:text-gray-300">
                    {item.label}
                  </div>
                  {item.subtext && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                      {item.subtext}
                    </div>
                  )}
                </Link>
              ))}
            </div>

            {/* Mobile Demos Section */}
            <div className="py-2">
              <div className="font-semibold text-glam-primary dark:text-glam-light mb-2">Demos</div>
              {demosDropdown.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className="block py-2 pl-4 hover:text-glam-accent dark:hover:text-glam-accent transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <div className="text-sm font-medium text-glam-secondary dark:text-gray-300">
                    {item.label}
                  </div>
                  {item.subtext && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                      {item.subtext}
                    </div>
                  )}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;